import { LightPresetNameType } from "@/utils/filterPreset";
import HENGE from "@henge-inc/global-types";
import { Environment } from "@react-three/drei";
import { LUTProps } from "@react-three/postprocessing/dist/effects/LUT";
import { BlendFunction, KernelSize, ToneMappingMode } from "postprocessing";
import React from "react";
import * as THREE from "three";

export const FilterName = {
  Default: "Default_241217",
  Jobs: "Jobs_241217",
  Kubrick: "Kubrick_241217",
  Miyazaki: "Miyazaki_241217",
  Nolan: "Nolan_241217",
} as const;
export type FilterNameType = HENGE.ValuesTypeFromObject<typeof FilterName>;

export const FilterBackgroundName = {
  Color: "Color",
  Image: "Image",
} as const;
export type FilterBackgroundNameType = HENGE.ValuesTypeFromObject<
  typeof FilterBackgroundName
>;
export type FilterColorBackground = {
  type: typeof FilterBackgroundName.Color;
  hex: string;
};
export type FilterImageBackground = {
  type: typeof FilterBackgroundName.Image;
  url: string;
  file?: File;
};
export type FilterBackground = FilterColorBackground | FilterImageBackground;

export const LightName = {
  AmbientLight: "AmbientLight",
  DirectionalLight: "DirectionalLight",
  PointLight: "PointLight",
  SpotLight: "SpotLight",
} as const;
export type LightNameType = HENGE.ValuesTypeFromObject<typeof LightName>;

type DefaultLightPropsDto = {
  name: string;
  color: string;
  intensity: number;
  castShadow?: boolean;
};
export type AmbientLightPropsDto = DefaultLightPropsDto & {
  type: typeof LightName.AmbientLight;
};
export type DirectionalLightPropsDto = DefaultLightPropsDto & {
  type: typeof LightName.DirectionalLight;
  shadow?: DirectionalLightShadowDto;
  position?: THREE.Vector3;
  // target?: THREE.Vector3;
};
export type PointLightPropsDto = DefaultLightPropsDto & {
  type: typeof LightName.PointLight;
  shadow?: PointLightShadowDto;
  position?: THREE.Vector3;
  distance?: number;
  decay?: number;
  power?: number;
};
export type SpotLightPropsDto = DefaultLightPropsDto & {
  type: typeof LightName.SpotLight;
  shadow?: SpotLightShadowDto;
  position?: THREE.Vector3;
  // target?: THREE.Vector3;
  angle?: number;
  decay?: number;
  power?: number;
  penumbra?: number;
};
export type LightPropsDto =
  | AmbientLightPropsDto
  | DirectionalLightPropsDto
  | PointLightPropsDto
  | SpotLightPropsDto;

type LightShadowDto = {
  "shadow-intensity"?: number;
  "shadow-bias"?: number;
  "shadow-normalBias"?: number;
  "shadow-radius"?: number;
  "shadow-mapSize-width"?: number;
  "shadow-mapSize-height"?: number;
};
export type DirectionalLightShadowDto = LightShadowDto & {
  "shadow-camera-zoom"?: number;
  "shadow-camera-left"?: number;
  "shadow-camera-right"?: number;
  "shadow-camera-top"?: number;
  "shadow-camera-bottom"?: number;
  "shadow-camera-near"?: number;
  "shadow-camera-far"?: number;
};
export type PointLightShadowDto = LightShadowDto & {
  "shadow-camera-zoom"?: number;
  "shadow-camera-fov"?: number;
  "shadow-camera-aspect"?: number;
  "shadow-camera-near"?: number;
  "shadow-camera-far"?: number;
};
export type SpotLightShadowDto = LightShadowDto & {
  "shadow-camera-zoom"?: number;
  "shadow-camera-fov"?: number;
  "shadow-camera-aspect"?: number;
  "shadow-camera-near"?: number;
  "shadow-camera-far"?: number;
};

export interface VaultObjectFilterCamera {
  readonly type: "PerspectiveCamera";
  readonly fov: number;
}

export interface VaultObjectFilterTurntable {
  readonly speed: number;
  readonly showHengeTile: boolean;
}

export const PostprocessingEffectName = {
  DepthOfField: "DepthOfField",
  ToneMapping: "ToneMapping",
  BrightnessContrast: "BrightnessContrast",
  LUT: "LUT",
  Noise: "Noise",
  Bloom: "Bloom",
} as const;
export type PostprocessingEffectNameType = HENGE.ValuesTypeFromObject<
  typeof PostprocessingEffectName
>;

export type PostprocessingEffectProps = {
  [PostprocessingEffectName.DepthOfField]: {
    blendFunction?: BlendFunction | undefined;
    worldFocusDistance?: number | undefined;
    worldFocusRange?: number | undefined;
    focusDistance?: number | undefined;
    focalLength?: number | undefined;
    focusRange?: number | undefined;
    bokehScale?: number | undefined;
    resolutionScale?: number | undefined;
    resolutionX?: number | undefined;
    resolutionY?: number | undefined;
    width?: number | undefined;
    height?: number | undefined;
  } & Partial<{
    blur: number;
  }>;
  [PostprocessingEffectName.ToneMapping]: {
    blendFunction?: BlendFunction | undefined;
    adaptive?: boolean | undefined;
    mode?: ToneMappingMode | undefined;
    resolution?: number | undefined;
    maxLuminance?: number | undefined;
    whitePoint?: number | undefined;
    middleGrey?: number | undefined;
    minLuminance?: number | undefined;
    averageLuminance?: number | undefined;
    adaptationRate?: number | undefined;
  };
  [PostprocessingEffectName.BrightnessContrast]: {
    blendFunction?: BlendFunction | undefined;
    brightness?: number | undefined;
    contrast?: number | undefined;
  };
  [PostprocessingEffectName.LUT]: Omit<
    LUTProps,
    "lut" | "tetrahedralInterpolation"
  > & {
    name: string;
  };
  [PostprocessingEffectName.Noise]: {
    blendFunction?: BlendFunction | undefined;
    premultiply?: boolean | undefined;
  } & { opacity?: number | undefined };
  [PostprocessingEffectName.Bloom]: {
    intensity?: number | undefined;
    kernelSize?: KernelSize;
    blendFunction?: BlendFunction | undefined;
    luminanceThreshold?: number;
    luminanceSmoothing?: number;
    mipmapBlur: boolean;
  };
};

export type EnvironmentProps = Pick<
  React.ComponentProps<typeof Environment>,
  "environmentIntensity" | "files"
>;

export interface VaultObjectFilter {
  name: string;
  strength: number;
  background: FilterBackground;
  camera: VaultObjectFilterCamera;
  turntable?: VaultObjectFilterTurntable;
  lightPreset: {
    name: LightPresetNameType;
    factor: number;
    lights: LightPropsDto[];
    environment?: EnvironmentProps;
  };
  postprocessing: { effects: Partial<PostprocessingEffectProps> };
}

export type VaultObjectFilterControllableFactor = keyof Pick<
  VaultObjectFilter,
  "strength" | "background" | "lightPreset" | "camera" | "turntable"
>;
